import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { GroupMemberChangeablePermissions } from '@weblite/ts-action/subscription'
import {
  changeMemberPermissions,
  decodeGroupMemberPermissions,
  decodeSubscriptionPermissions,
  getExceptionMemberPermission,
  removeMembers,
} from '$logic/subscription'
import {
  GROUP_MEMBER_PERMISSIONS,
  HallPermissionsPage,
} from '$common/hall-permissions-page'
import { t } from '$i18n'
import { useAsyncFunction } from '$func/async-function.react-hook'
import {
  closeChannelAdminPermissions,
  closeGroupMemberPermissionsOverride,
} from '$logic/router'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import { getRoom } from '$logic/room'

interface Props {
  userId: string
  hallId: string
  mode: 'DEFINE' | 'EDIT'
}

export function GroupMemberPermissionOverride({ userId, hallId, mode }: Props) {
  const useAsyncFunctionCallBack =
    mode === 'DEFINE'
      ? () => decodeSubscriptionPermissions(hallId)
      : async () => {
          const memberPermission = await getExceptionMemberPermission(
            hallId,
            userId,
          )
          return decodeGroupMemberPermissions(hallId, memberPermission)
        }

  const { value: initialPermissions } = useAsyncFunction<
    GroupMemberChangeablePermissions
  >(useAsyncFunctionCallBack, [hallId, userId])

  const changeablePermissions = useClientUserPermissions(hallId)

  const onComplete = async (permissions: GroupMemberChangeablePermissions) => {
    showSaveChangesLoadingModal()
    await changeMemberPermissions(hallId, userId, permissions)
    await closeCurrentModal()
    await closeChannelAdminPermissions()
  }

  const onRemoveMember = async () => {
    showSaveChangesLoadingModal()
    const room = await getRoom(hallId)
    analite('CHAT_REMOVE_MEMBER', {
      type: room?.type,
      source: 'GROUP_MEMBER_PERMISSIONS',
    })
    await removeMembers(hallId, [userId])
    await closeCurrentModal()
    await closeChannelAdminPermissions()
  }

  return (
    <HallPermissionsPage
      pageTitle={t('hallPermissionsPage.groupMemberPermissionsOverride.title')}
      confirmDisabled={false}
      subTitle={t(
        'hallPermissionsPage.groupMemberPermissionsOverride.subTitle',
      )}
      rights={GROUP_MEMBER_PERMISSIONS.map(right => ({
        ...right,
        isLocked: !changeablePermissions?.[right.id],
      }))}
      actionButtonProps={{
        text: t(
          'hallPermissionsPage.groupMemberPermissionsOverride.actionButtonText',
        ),
        color: 'error',
      }}
      userId={userId}
      initialPermissions={initialPermissions}
      onBack={closeGroupMemberPermissionsOverride}
      onComplete={onComplete}
      onActionButtonClick={onRemoveMember}
    />
  )
}
